/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import {
    Box,
    CircularProgress,
    IconButton,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf, faFileInvoice } from '@fortawesome/free-solid-svg-icons';

import {
    getPDF,
    getSFMCContent,
    getWorkOrderDocuments
} from "../services/api";
import WorkOrderDocuments from "./WorkOrderDocuments";
import PDFModal from "./PDFModal";

const WORK_ORDER_TYPE = 'workOrder';
const SFCC_SOURCE_TYPE = 'SFCC';

export default function PDFCell({ id, dataType, source, timeZone, documentDate, features, autoOpen, autoOpenVersion, onOpenDocument }) {
    const [isLoading, setIsLoading] = useState(false);
    const [workOrderDocumentLoading, setWorkOrderDocumentLoading] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [pdfData, setPDFData] = useState(null);
    const [workOrderDocumentsModalOpen, setWorkOrderDocumentsModalOpen] = useState(false);
    const [workOrderDocumentData, setWorkOrderDocumentData] = useState([]);
    const [error, setError] = useState(null);

    const { displaySFMCContent = false } = features || {};

    useEffect(() => {
        const closeModal = (e) => {
            if (e.keyCode === 27) {
              handleModalClose();
              handleWorkOrderDocumentModalClose();
            }
        }
        window.addEventListener('keydown', closeModal);
        return () => window.removeEventListener('keydown', closeModal);
    }, [])

    useEffect(() => {
        if (autoOpen) {
            handleClick();
        }
    }, [autoOpen])

    const handleClick = async () => {
        setIsLoading(true);
        try {
            if (dataType === WORK_ORDER_TYPE) {
                const documentData = await getWorkOrderDocuments(id);
                setWorkOrderDocumentData(documentData);
                const match = autoOpenVersion !== undefined && autoOpenVersion !== null
                    ? (documentData || []).find((doc) => String(doc.version) === String(autoOpenVersion))
                    : null;
                if (match) {
                    await getWorkOrderPDF(match.key, match.version);
                } else {
                    setWorkOrderDocumentsModalOpen(true);
                    if (onOpenDocument) {
                        onOpenDocument(dataType, id);
                    }
                }
            } else if (source === SFCC_SOURCE_TYPE) {
                if (!displaySFMCContent) {
                    throw new Error('SFMC Content Not Found')
                } else {
                    const data = await getSFMCContent(id, dataType, source, documentDate);
                    setPDFData(data);
                    setModalOpen(true);
                    if (onOpenDocument) {
                        onOpenDocument(dataType, id);
                    }
                }
            } else {
                const data = await getPDF(id, dataType, source);
                setPDFData(data);
                setModalOpen(true);
                if (onOpenDocument) {
                    onOpenDocument(dataType, id);
                }
            }
        } catch (error) {
            setError(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleModalClose = () => {
        setPDFData(null);
        setModalOpen(false);
        setError(null);
    }

    const handleWorkOrderDocumentModalClose = () => {
        setWorkOrderDocumentData([]);
        setWorkOrderDocumentsModalOpen(false);
        setError(null);
    }

    const getWorkOrderPDF = async (documentKey, version) => {
        setWorkOrderDocumentLoading(documentKey);
        try {
            const data = await getPDF(documentKey, WORK_ORDER_TYPE);
            setPDFData(data);
            setModalOpen(true);
            if (onOpenDocument) {
                const resolvedVersion = version !== undefined
                    ? version
                    : (workOrderDocumentData.find((doc) => doc.key === documentKey) || {}).version;
                onOpenDocument(dataType, id, resolvedVersion);
            }
        } catch (error) {
            setModalOpen(false);
            console.error(error.message);
        } finally {
            setWorkOrderDocumentLoading(null);
        }
    }

    return (
        <>
            <Box display="flex" alignItems="center">
                <Box>
                    {isLoading ? <CircularProgress /> :
                        <IconButton onClick={handleClick}>
                            <FontAwesomeIcon icon={(source === 'SFCC' && displaySFMCContent) ? faFileInvoice : faFilePdf} />
                        </IconButton>
                    }
                </Box>
            </Box>
            <PDFModal
                id={id}
                dataType={dataType}
                source={source}
                open={modalOpen || error}
                data={pdfData}
                handleClose={handleModalClose}
                error={error}
            />
            <WorkOrderDocuments
                id={id}
                dataType={dataType}
                timeZone={timeZone}
                open={workOrderDocumentsModalOpen || error}
                documents={workOrderDocumentData}
                handleClose={handleWorkOrderDocumentModalClose}
                handleWorkOrderButtonClick={(id) => getWorkOrderPDF(id)}
                workOrderDocumentLoading={workOrderDocumentLoading}
                error={error}
            />
        </>
    )
}